import React, { useMemo, useState } from "react";
import {
  Tabs,
  Table,
  Player,
  Tournament,
  Match,
  Avatar,
  Button,
  Spinner,
} from "../components/handball";
import AutoBracketWithResults from "../components/handball/auto-bracket";
import "./handball.scss";
import { useLocation, useNavigate } from "react-router-dom";
import Rankings from "./handball/rankings";
import Tournaments from "./handball/tournaments";
import HandballHeader from "./handball/header";
import { pad } from "../internal/handball/utils";
import { buildMatchesForTournament } from "../internal/handball/matches";
import { computeRatings } from "../internal/handball/elo";
import { simulateBracketResults } from "../internal/handball/simulate";

/**
 * Mock data (replace with your API/DB later)
 * - 300 players
 * - 4 tournaments with participant sizes: 32, 28, 16, 22
 * - Single elimination brackets with automatic byes
 */
const TOTAL_PLAYERS = 300;
// Deterministic pseudo-random names so avatars show meaningful initials
const FIRST_NAMES = [
  "Ava", "Liam", "Mia", "Noah", "Emma", "Oliver", "Sophia", "Elijah", "Isabella", "James",
  "Amelia", "William", "Charlotte", "Benjamin", "Evelyn", "Lucas", "Harper", "Henry", "Abigail", "Michael",
];
const LAST_NAMES = [
  "Johnson", "Kim", "Patel", "Chen", "Rivera", "Singh", "Torres", "Brooks", "Diaz", "Nguyen",
  "Clark", "Lee", "Hernandez", "Martinez", "Davis", "Lopez", "Gonzalez", "Wilson", "Anderson", "Thomas",
];
const players: Player[] = Array.from({ length: TOTAL_PLAYERS }, (_, i) => {
  const id = `p${i + 1}`;
  const first = FIRST_NAMES[i % FIRST_NAMES.length];
  const last = LAST_NAMES[Math.floor(i / FIRST_NAMES.length) % LAST_NAMES.length];
  return { id, name: `${first} ${last}` };
});

// Top-10 seeds (example data). Keys are player IDs; values are seed numbers.
const TOP_SEEDS: Record<string, number> = {
  p1: 1,
  p2: 2,
  p3: 3,
  p4: 4,
  p5: 5,
  p6: 6,
  p7: 7,
  p8: 8,
  p9: 9,
  p10: 10,
};

// Friendly display names for seeded players
const TOP_SEED_NAMES: Record<string, string> = {
  p1: "Calvin Lee",
  p2: "Alex Kim",
  p3: "Jordan Park",
  p4: "Riley Chen",
  p5: "Noah Diaz",
  p6: "Taylor Singh",
  p7: "Sam Patel",
  p8: "Avery Brooks",
  p9: "Maya Torres",
  p10: "Ethan Rivera",
};

const sizes = [32, 28, 16, 22];
const participantsByTournament: Record<string, string[]> = {
  t1: Array.from({ length: sizes[0] }, (_, i) => `p${i + 1}`),
  t2: Array.from({ length: sizes[1] }, (_, i) => `p${40 + i + 1}`),
  t3: Array.from({ length: sizes[2] }, (_, i) => `p${100 + i + 1}`),
  t4: Array.from({ length: sizes[3] }, (_, i) => `p${180 + i + 1}`),
  // upcoming
  u1: Array.from({ length: 18 }, (_, i) => `p${220 + i + 1}`),
  u2: Array.from({ length: 14 }, (_, i) => `p${260 + i + 1}`),
};

const tournaments: Tournament[] = [
  { id: "t1", name: "Tompkins Square Open", date: "2025-08-03" },
  { id: "t2", name: "West 4th Street Classic", date: "2025-08-10" },
  { id: "t3", name: "Coney Island Summer Slam", date: "2025-08-17" },
  { id: "t4", name: "Riverside Park Invitational", date: "2025-08-24" },
];

const UPCOMING_TOURNAMENTS: Array<Tournament & { count: number; }> = [
  { id: "u1", name: "Prospect Park Fall Classic (18 players)", date: "2025-09-07", count: 18 },
  { id: "u2", name: "Astoria Courts Showdown (14 players)", date: "2025-09-21", count: 14 },
];

const matches: Match[] = [
  ...buildMatchesForTournament("t1", participantsByTournament.t1),
  ...buildMatchesForTournament("t2", participantsByTournament.t2),
  ...buildMatchesForTournament("t3", participantsByTournament.t3),
  ...buildMatchesForTournament("t4", participantsByTournament.t4),
];

// (Elo helpers removed; using internal/handball/elo.computeRatings)

function TournamentDetail({ id, onClose }: { id: string; onClose: () => void; }) {
  const tourney = tournaments.find(t => t.id === id) || UPCOMING_TOURNAMENTS.find(t => t.id === id)!;
  const tourneyMatches = matches.filter(m => m.tournamentId === id);
  const playerIds = Array.from(new Set(tourneyMatches.flatMap(m => [m.player1Id, m.player2Id])));

  const wl = useMemo(() => {
    const map = new Map<string, { wins: number; losses: number; }>();
    for (const pid of playerIds) map.set(pid, { wins: 0, losses: 0 });
    for (const m of tourneyMatches) {
      const p1 = m.player1Id, p2 = m.player2Id;
      const w = m.winnerId, l = w === p1 ? p2 : p1;
      map.get(w)!.wins++;
      map.get(l)!.losses++;
    }
    return map;
  }, [id]);

  // build players list for AutoBracket (strings are accepted)
  const participantIds = (participantsByTournament as Record<string, string[]>)[id] || [];
  const bracketPlayers = participantIds.map(pid => {
    const num = Number(pid.replace(/\D/g, ""));
    const name = TOP_SEED_NAMES[pid] ?? `Player ${String(num).padStart(2, "0")}`;
    return { id: pid, name };
  });

  // Rank players by seeds (top 10 fixed, others follow)
  const seedById: Record<string, number> = {};
  const seededPresent = participantIds.filter(pid => TOP_SEEDS[pid] != null).sort((a, b) => TOP_SEEDS[a] - TOP_SEEDS[b]);
  seededPresent.forEach((pid, idx) => { seedById[pid] = TOP_SEEDS[pid]; });
  let nextSeed = 1;
  // ensure nextSeed starts after max present seed
  const maxSeed = Math.max(0, ...Object.values(seedById));
  nextSeed = Math.max(nextSeed, maxSeed + 1);
  participantIds.forEach(pid => { if (seedById[pid] == null) { seedById[pid] = nextSeed++; } });

  // Build ranked list by ascending seed number
  const rankedPlayers = [...bracketPlayers].sort((a, b) => (seedById[a.id] || 1e9) - (seedById[b.id] || 1e9));

  const isUpcoming = !!UPCOMING_TOURNAMENTS.find(t => t.id === id);
  const results = useMemo(() => (isUpcoming ? [] : simulateBracketResults(rankedPlayers.map(p => p.id))), [id, rankedPlayers]);

  const [detailTab, setDetailTab] = useState<string>("bracket");

  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="u-row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div className="u-row" style={{ gap: 10, alignItems: "baseline" }}>
          <h3 className="card__header" style={{ margin: 0 }}>{tourney?.name}</h3>
          <div className="card__meta" style={{ fontSize: 14, fontWeight: 700 }}>{new Date(tourney?.date).toLocaleDateString()}</div>
        </div>
        <button className="btn btn--ghost" onClick={onClose}>Back</button>
      </div>
      <div className="u-row" style={{ marginTop: 8 }}>
        <Tabs
          tabs={[{ id: "bracket", label: "Bracket" }, { id: "players", label: "Players" }]}
          activeId={detailTab}
          onChange={(idSel: string) => setDetailTab(idSel)}
        />
      </div>

      {detailTab === "bracket" && (
        <div style={{ marginTop: 8, overflow: "auto", maxHeight: "70vh", WebkitOverflowScrolling: "touch" as any }}>
          <AutoBracketWithResults players={rankedPlayers} results={results} />
        </div>
      )}

      {detailTab === "players" && (
        <div style={{ marginTop: 8 }}>
          <h4 className="card__header" style={{ fontSize: 18 }}>Players</h4>
          <Table>
            <thead>
              <tr>
                <th>Player</th>
                {isUpcoming ? <th>Seed</th> : <><th>W</th><th>L</th></>}
              </tr>
            </thead>
            <tbody>
              {isUpcoming ? (
                rankedPlayers.map(p => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td>{seedById[p.id]}</td>
                  </tr>
                ))
              ) : (
                playerIds.map(pid => {
                  const p = players.find(x => x.id === pid)!;
                  const rec = wl.get(pid) || { wins: 0, losses: 0 };
                  return (
                    <tr key={pid}>
                      <td>{p.name}</td>
                      <td>{rec.wins}</td>
                      <td>{rec.losses}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default function Handball(): JSX.Element {
  const [tab, setTab] = useState<string>("rankings");
  const [openTournamentId, setOpenTournamentId] = useState<string | null>(null);
  const [me, setMe] = useState<{ authenticated: boolean; user?: { name?: string; picture?: string; email?: string; }; } | null>(null);
  const [loginOpen, setLoginOpen] = useState<boolean>(false);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();

  const ratingRows = useMemo(() => computeRatings(players, matches, tournaments), []);
  const sortedTournaments = useMemo(() => [...tournaments].sort((a, b) => b.date.localeCompare(a.date)), []);

  React.useEffect(() => {
    const p = location.pathname;
    if (p === "/handball" || p === "/handball/") {
      navigate("/handball/rankings", { replace: true });
      return;
    }
    if (p.startsWith("/handball/")) {
      const parts = p.split("/");
      const seg = parts[2];
      if (seg === "rankings" && tab !== "rankings") { setTab("rankings"); setOpenTournamentId(null); }
      if (seg === "tournaments") {
        setTab("tournaments");
        const tId = parts[3];
        if (tId) setOpenTournamentId(tId);
      }
    }
  }, [location.pathname]);

  React.useEffect(() => {
    fetch("/api/me", { credentials: "include" })
      .then(async r => {
        const body = await r.json().catch(() => ({} as any));
        if (body && body.reauth) {
          try { await fetch('/auth/logout', { method: 'POST', credentials: 'include' }); } catch { }
          setMe({ authenticated: false } as any);
          window.location.reload();
          return;
        }
        setMe(body);
      })
      .catch(() => setMe({ authenticated: false } as any));
  }, []);

  return (
    <div className="hb-container handball-container">
      <HandballHeader
        title="NYC Handball Hub"
        subtitle="Rankings, tournaments, and brackets for the NYC scene"
        right={
          <div className="u-row" style={{ gap: 12, alignItems: "center" }}>
            <Tabs
              tabs={[{ id: "rankings", label: "Rankings" }, { id: "tournaments", label: "Tournaments" }]}
              activeId={tab}
              onChange={(id: string) => { setOpenTournamentId(null); setTab(id); navigate(`/handball/${id}`); }}
            />
            <div aria-hidden="true" style={{ width: 2, height: 28, background: "var(--gray-400)", borderRadius: 2, opacity: 0.9 }} />
            {me == null ? (
              <Spinner />
            ) : me.authenticated && me.user ? (
              <div className="u-row" style={{ gap: 8, alignItems: "center", position: "relative" }}>
                {me.user.picture ? (
                  <img src={me.user.picture} alt={me.user.name || ""} title={me.user.name || ""} style={{ width: 28, height: 28, borderRadius: "50%" }} />
                ) : (
                  <Avatar name={me.user.name || "User"} />
                )}
                <div style={{ fontWeight: 800, fontSize: 14 }} title={(me as any)?.user?.email || ""}>{me.user.name || ""}</div>
                <button aria-label="Settings" onClick={() => setSettingsOpen(v => !v)} style={{ padding: 8, background: "transparent", border: "none", outline: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8 }}>
                  <span aria-hidden style={{ display: "inline-block", width: 20, height: 20 }}>
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3"></circle>
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0A1.65 1.65 0 0 0 9 3.09V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82h0A1.65 1.65 0 0 0 21 12h.09a2 2 0 1 1 0 4H21a1.65 1.65 0 0 0-1.6-1z"></path>
                    </svg>
                  </span>
                </button>
                {settingsOpen && (
                  <div className="card" style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, padding: 10, width: 220 }}>
                    <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 6 }}>Account</div>
                    <div style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 8 }}>{(me as any)?.user?.email || ""}</div>
                    <Button
                      variant="ghost"
                      onClick={() => { fetch('/auth/logout', { method: 'POST', credentials: 'include' }).finally(() => window.location.reload()); }}
                      style={{ width: "100%", padding: "6px 10px", fontSize: 13, fontWeight: 700 }}
                    >
                      Logout
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <Button onClick={() => setLoginOpen(true)}>Login</Button>
            )}
          </div>
        }
      />

      {tab === "rankings" && (
        <Rankings rows={ratingRows} topPlayer={players[0]} />
      )}

      {tab === "tournaments" && (
        openTournamentId ? (
          <TournamentDetail id={openTournamentId} onClose={() => { setOpenTournamentId(null); navigate("/handball/tournaments"); }} />
        ) : (
          <Tournaments
            players={players}
            tournaments={sortedTournaments}
            matches={matches}
            participantsByTournament={participantsByTournament}
            topSeedNames={TOP_SEED_NAMES}
            topSeeds={TOP_SEEDS}
            onOpen={(id: string) => { setOpenTournamentId(id); navigate(`/handball/tournaments/${id}`); }}
            openTournamentId={null}
            onClose={() => { setOpenTournamentId(null); navigate("/handball/tournaments"); }}
          />
        )
      )}

      <div style={{ marginTop: 20, fontSize: 12, color: "#6b7280" }}>
        <div>Demo data in-memory. Wire this up to your API/DB when ready.</div>
        <div>Suggested next steps: replace mock data with fetch calls, add authenticated admin to input results, and persist Elo after each match.</div>
      </div>

      {loginOpen && (
        <div className="modal-backdrop" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div className="card" style={{ width: 420, padding: 20, borderRadius: 14, boxShadow: "0 10px 30px rgba(0,0,0,0.12)" }}>
            <div className="u-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
              <h3 className="card__header" style={{ margin: 0, fontSize: 18 }}>Sign in</h3>
              <button aria-label="Close" onClick={() => setLoginOpen(false)} style={{ background: "transparent", border: "1px solid var(--border-subtle)", padding: "4px 8px", borderRadius: 10, cursor: "pointer", fontWeight: 800 }}>×</button>
            </div>
            <div style={{ marginTop: 10, color: "var(--text-secondary)", lineHeight: 1.5, fontSize: 15 }}>
              Use your Google account to continue.
            </div>
            <div style={{ marginTop: 16 }}>
              <Button onClick={() => { window.location.href = "/api/auth/login/google"; }}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "10px 14px", fontSize: 15 }}>
                <span aria-hidden style={{ display: "inline-flex", width: 18, height: 18, alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.2)", borderRadius: 4 }}>
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                    <path d="M12 2a10 10 0 1 0 9.95 11.16h-9.2v-3.2h8.84A8.8 8.8 0 1 1 12 3.2" />
                  </svg>
                </span>
                Continue with Google
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
